import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import api from '../api';
import { useGet, useGetOne, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';
import { Context as AuthContext } from '../../auth/context';
import { Airline } from '../types';

import Context from './context';

interface Props {
    children: React.ReactNode;
}

const AirlinesProvider: React.FC<Props> = ({ children }: Props) => {
    const [data, setData] = useState<Array<Airline>>([]);
    const [selected, setSelected] = useState<Airline | null>(null);
    const [error, setError] = useState({});
    const authContext = useContext(AuthContext);
    const fetched = useRef(false);

    const handleGetData = useCallback((res) => {
        setData(res);
        return res;
    }, []);
    const get = useGet(api, data, handleGetData, setError);

    // Load once on auth — airlines are static lookup data
    useEffect(() => {
        if (authContext.currUser && !fetched.current) {
            fetched.current = true;
            get({});
        }
    }, [authContext.currUser]); // eslint-disable-line

    const getOne = useGetOne(api, setSelected, setError);

    const handleSetData = useCallback((item, newData) => {
        setData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const put = usePut(api, data, handleSetData, setError);

    const handleDeleteCallback = useCallback((id, newData) => {
        setData(newData);
        setSelected(null);
        return id;
    }, []);
    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    return (
        <Context.Provider
            value={{
                get,
                getOne,
                post,
                put,
                remove,
                softDelete,
                data,
                setData,
                selected,
                setSelected,
                error
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default AirlinesProvider;
